import { useEffect } from 'react'
import { geoJsonLayerId, useMap } from '@wehunt/map'
import { GROUND_HIT_ID, LINES_HIT_ID, PARCEL_FILL_ID } from './editorLayerIds'
import type { LayerId, PlaceMode } from './mapLibreEditorTypes'

/** The minimal shape this router reads off `map.on('click', ...)`'s payload —
 *  MapLibre's real `MapMouseEvent` carries both, and the fake's `fire()`
 *  lets a consumer test hand exactly this shape in. */
interface ClickPayload {
  lngLat: { lng: number; lat: number }
  point: { x: number; y: number }
}

type GroundKind = 'border' | 'subarea' | 'forbidden'

/** Controller ruling: ground kind precedence is resolved by THREE separate,
 *  kind-filtered `queryRenderedFeatures` calls in a fixed priority order
 *  (border → forbidden → subarea, first hit wins) — never by trusting
 *  MapLibre's same-layer feature return order, which the style spec does not
 *  document as stable. The `filter` query option does the real work on the
 *  production engine (restricting the returned set server-side to features
 *  of exactly this `kind`); the `.find()` below is a second, redundant-on-
 *  production check that ALSO makes this deterministic against the
 *  `@wehunt/map/testing` fake, whose `queryRenderedFeatures` ignores query
 *  options entirely and returns whatever `setFakeQueriedFeatures` configured
 *  regardless of `filter` — so a test can stub one array containing both an
 *  overlapping border and sub-area feature and this function still resolves
 *  the correct one on every one of the three calls. */
function queryGroundHit(
  map: NonNullable<ReturnType<typeof useMap>>,
  point: { x: number; y: number },
  kind: GroundKind,
): { kind: GroundKind; id: string } | null {
  const hits = map.queryRenderedFeatures([point.x, point.y], {
    layers: [geoJsonLayerId(GROUND_HIT_ID)],
    filter: ['==', ['get', 'kind'], kind],
  })
  const match = hits.find((h) => h.properties.kind === kind)
  return match ? { kind, id: String(match.properties.id) } : null
}

export interface EditorClickRoutingProps {
  drawing: boolean
  placeMode: PlaceMode
  onIdentifyProperty?: (lat: number, lon: number) => void
  onAddDrawPoint: (lat: number, lon: number) => void
  onAddObj: (lat: number, lon: number) => void
  onToggleParcel: (id: string) => void
  showParcels: boolean
  activeTab: LayerId
  onSelectObj: (id: string | null) => void
  onPolygonSelect: (kind: 'border' | 'subarea' | 'forbidden', id: string) => void
  onLineSelect?: (id: string) => void
  /** Whether the combined ground hit layer (`GROUND_HIT_ID`) is currently
   *  mounted — must mirror `EditorGroundLayers`'s own `clickable` gate
   *  exactly, or a query against a layer id that was never added throws on
   *  the real engine. */
  groundHitLayerMounted: boolean
  /** Mirrors `EditorPinsAndLines`'s own `linesClickable` gate — same reason. */
  linesHitLayerMounted: boolean
}

/**
 * The single `map.on('click', ...)` listener implementing
 * `FullscreenMapCanvas`'s `onSvgClick` precedence, rendered inside `MapView`'s
 * children so `useMap()` resolves to the live map instance:
 *
 *   1. drawing            → `onAddDrawPoint`
 *   2. placeMode           → `onAddObj`
 *   3. a candidate parcel outline is hit (properties mode) → `onToggleParcel`
 *      (mirrors the old canvas's parcel `<g onClick>` intercepting BEFORE its
 *      click could bubble to the top-level handler below)
 *   4. `onIdentifyProperty` is present → `onIdentifyProperty` (gated on the
 *      callback's mere PRESENCE, exactly like the old canvas — the parent
 *      only ever passes it while in properties mode)
 *   5. else: hit-test the lines layer → `onLineSelect`, then the ground hit
 *      layer, queried border → forbidden → subarea (first hit wins,
 *      `queryGroundHit`) → `onPolygonSelect`
 *   6. nothing hit and `activeTab === 'pins'` → `onSelectObj(null)`
 *
 * Pins and parcel markers intercept their own clicks (`stopPropagation`, see
 * `EditorPinsAndLines`/`EditorParcelLayer`) and never reach this handler —
 * matching the old canvas's per-shape `onClick` + `e.stopPropagation()`.
 */
export function EditorClickRouting({
  drawing,
  placeMode,
  onIdentifyProperty,
  onAddDrawPoint,
  onAddObj,
  onToggleParcel,
  showParcels,
  activeTab,
  onSelectObj,
  onPolygonSelect,
  onLineSelect,
  groundHitLayerMounted,
  linesHitLayerMounted,
}: EditorClickRoutingProps) {
  const map = useMap()

  useEffect(() => {
    if (!map) return
    const handleClick = (payload: unknown) => {
      const { lngLat, point } = payload as ClickPayload
      const lat = lngLat.lat
      const lon = lngLat.lng

      if (drawing) {
        onAddDrawPoint(lat, lon)
        return
      }
      if (placeMode) {
        onAddObj(lat, lon)
        return
      }
      if (showParcels) {
        const parcelHit = map.queryRenderedFeatures([point.x, point.y], { layers: [geoJsonLayerId(PARCEL_FILL_ID)] })[0]
        if (parcelHit) {
          onToggleParcel(String(parcelHit.properties.id))
          return
        }
      }
      if (onIdentifyProperty) {
        onIdentifyProperty(lat, lon)
        return
      }

      if (linesHitLayerMounted) {
        const lineHit = map.queryRenderedFeatures([point.x, point.y], { layers: [geoJsonLayerId(LINES_HIT_ID)] })[0]
        if (lineHit) {
          onLineSelect?.(String(lineHit.properties.id))
          return
        }
      }

      if (groundHitLayerMounted) {
        // Sequential, not one combined query: see `queryGroundHit`'s doc
        // comment for why. `??` short-circuits — forbidden is only ever
        // queried when no border matched, subarea only when neither did.
        const groundHit =
          queryGroundHit(map, point, 'border') ??
          queryGroundHit(map, point, 'forbidden') ??
          queryGroundHit(map, point, 'subarea')
        if (groundHit) {
          onPolygonSelect(groundHit.kind, groundHit.id)
          return
        }
      }

      if (activeTab === 'pins') onSelectObj(null)
    }
    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
    }
  }, [
    map,
    drawing,
    placeMode,
    onIdentifyProperty,
    onAddDrawPoint,
    onAddObj,
    onToggleParcel,
    showParcels,
    activeTab,
    onSelectObj,
    onPolygonSelect,
    onLineSelect,
    groundHitLayerMounted,
    linesHitLayerMounted,
  ])

  return null
}
